import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Sun, Moon, Package, Megaphone, Check, X } from 'phosphor-react';
import useNotifications from '../use/useNotifications';
import usePresence from '../use/usePresence';
import Skeleton, { SkeletonArea } from './ui/Skeleton';
import Carregando from './ui/Carregando';
import EstadoVazio from './ui/EstadoVazio';
import { NotificationDTO, NotificationKind } from '../types/dtos';

/** Ícone por tipo — a origem do aviso se lê antes do texto. */
const ICONES: Record<NotificationKind, typeof Bell> = {
  ROUTINE_MORNING: Sun,
  ROUTINE_NIGHT: Moon,
  PRODUCT_EXPIRY: Package,
  ANNOUNCEMENT: Megaphone,
};

/** "agora", "há 3h", "há 2d" — data cheia numa lista de avisos é ruído. */
function quando(iso: string): string {
  const minutos = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (minutos < 1) return 'agora';
  if (minutos < 60) return `há ${minutos}min`;
  const horas = Math.floor(minutos / 60);
  if (horas < 24) return `há ${horas}h`;
  const dias = Math.floor(horas / 24);
  return dias === 1 ? 'ontem' : `há ${dias}d`;
}

export default function NotificationBell() {
  const [aberto, setAberto] = useState(false);
  const { montado, estado } = usePresence(aberto, 200);
  const navigate = useNavigate();
  const { itens, naoLidas, carregando, marcarComoLida, marcarTodasComoLidas } = useNotifications();

  const abrir = (n: NotificationDTO) => {
    if (!n.readAt) marcarComoLida(n.id);
    setAberto(false);
    if (n.link) navigate(n.link);
  };

  return (
    <>
      <button
        onClick={() => setAberto(true)}
        className="relative w-12 h-12 flex items-center justify-center rounded-full bg-white dark:bg-gray-800 shadow-soft dark:shadow-none border border-nude-100 dark:border-gray-700 text-blush-400 hover:text-blush-500 hover:shadow-glow-blush transition-[color,box-shadow] active:scale-[0.97]"
        aria-label={naoLidas > 0 ? `Notificações (${naoLidas} não lidas)` : 'Notificações'}
      >
        <Bell className="w-6 h-6" />
        {naoLidas > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[20px] h-5 px-1 flex items-center justify-center text-[10px] font-bold text-white bg-blush-500 rounded-full ring-2 ring-white dark:ring-gray-900">
            {naoLidas > 9 ? '9+' : naoLidas}
          </span>
        )}
      </button>

      {montado && (
        <div
          data-state={estado}
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center data-[state=closed]:pointer-events-none"
        >
          {/* Clique fora fecha: ao contrário do TermsGate, aqui não há nada a aceitar. */}
          <button
            data-state={estado}
            className="absolute inset-0 bg-black/40 backdrop-blur-sm data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out"
            onClick={() => setAberto(false)}
            aria-label="Fechar notificações"
          />

          <div
            role="dialog"
            aria-modal="true"
            data-state={estado}
            className="relative w-full sm:max-w-md bg-white dark:bg-gray-900 rounded-t-3xl sm:rounded-3xl shadow-soft-lg max-h-[75vh] flex flex-col data-[state=open]:animate-sheet-in data-[state=closed]:animate-sheet-out sm:data-[state=open]:animate-scale-in sm:data-[state=closed]:animate-scale-out"
          >
            <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-nude-100 dark:border-gray-700">
              <h2 className="text-base font-bold text-gray-800 dark:text-gray-100">Notificações</h2>
              <div className="flex items-center gap-1">
                {naoLidas > 0 && (
                  <button
                    onClick={() => marcarTodasComoLidas()}
                    className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-blush-500 hover:bg-blush-50 dark:hover:bg-gray-800 rounded-lg transition-colors"
                  >
                    <Check className="w-3.5 h-3.5" />
                    Marcar todas
                  </button>
                )}
                <button
                  onClick={() => setAberto(false)}
                  className="p-2 text-nude-400 dark:text-gray-500 hover:text-blush-500 rounded-lg transition-colors"
                  aria-label="Fechar"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            <Carregando
              quando={carregando}
              className="overflow-y-auto flex-1 px-2 py-2"
              esqueleto={
                <SkeletonArea className="px-3 py-2 flex flex-col gap-3" label="Carregando notificações">
                  {[0, 1, 2].map((i) => (
                    <div key={i} className="flex items-start gap-3">
                      <Skeleton className="w-9 h-9 rounded-xl flex-shrink-0" />
                      <div className="flex-1 flex flex-col gap-2 pt-1">
                        <Skeleton className="h-3 w-2/3" />
                        <Skeleton className="h-2.5 w-1/3" />
                      </div>
                    </div>
                  ))}
                </SkeletonArea>
              }
            >
            <div className="stagger">
              {itens.length === 0 && (
                <EstadoVazio
                  className="py-10"
                  icone={<Bell className="w-8 h-8 text-nude-300 dark:text-gray-600" />}
                  titulo="Nada por aqui ainda"
                  descricao="Os lembretes da sua rotina aparecem aqui."
                />
              )}

              {itens.map((n) => {
                const Icone = ICONES[n.kind];
                return (
                  <button
                    key={n.id}
                    onClick={() => abrir(n)}
                    className={`w-full flex items-start gap-3 px-3 py-3 rounded-2xl text-left transition-colors hover:bg-nude-50 dark:hover:bg-gray-800 ${
                      n.readAt ? '' : 'bg-blush-50/60 dark:bg-blush-900/10'
                    }`}
                  >
                    <div className="w-9 h-9 flex-shrink-0 rounded-xl bg-blush-50 dark:bg-blush-900/20 flex items-center justify-center">
                      <Icone className="w-4 h-4 text-blush-500 dark:text-blush-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between gap-2">
                        <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">
                          {n.title}
                        </p>
                        <span className="text-[10px] text-nude-400 dark:text-gray-500 flex-shrink-0">
                          {quando(n.createdAt)}
                        </span>
                      </div>
                      <p className="mt-0.5 text-xs leading-relaxed text-nude-500 dark:text-gray-400">
                        {n.body}
                      </p>
                    </div>
                    {!n.readAt && (
                      <span className="mt-1.5 w-2 h-2 rounded-full bg-blush-500 flex-shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>
            </Carregando>
          </div>
        </div>
      )}
    </>
  );
}
